import { useState } from "react";
// Components
import { Grid, Theme, Typography, styled } from "@mui/material";
import { useTheme } from "@mui/styles";
// API Calls
import { useEffect } from "react";
import { useSelector } from "react-redux";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { formatDateString } from "src/components/DateDisplay";
import { SpinnerProgress } from "src/components/SpinnerProgress/SpinnerProgress";
import { IRootState } from "src/redux/reducers/CombinedReducers";

interface IAPIVersionInfoWindowProps {
  id: string;
  path: string[];
}

interface IAPIStackInfo {
  stack: string;
  version: string;
  status: string;
  lastUpdated?: Date;
}

interface IStackVersionInfo {
  stacks: IAPIStackInfo[];
}

const OCAPIVersionInfoWindow = styled(Grid)(({ theme }) => ({
  backgroundColor: theme.custom.timeline.contextListBackground,
  width: "100%",

  "& .window_title": {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    "& .page_header": {
      margin: "5px 0px",
      "& .subheader": {
        margin: "0px 10px",
        color: theme.custom.forms.labels,
      },
    },
  },
}));

/**
 * Display API Version Info Window
 */
export const APIVersionInfoWindow = (props: IAPIVersionInfoWindowProps): JSX.Element => {
  const theme: Theme = useTheme();

  const userIsVMO = useSelector((state: IRootState) => state.authentication.attributes?.email?.includes("vmo.aero"));
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [stackInfo, setStackInfo] = useState<IAPIStackInfo[]>([]);

  useEffect(() => {
    if (userIsVMO) {
      const getStackInfo = async (): Promise<void> => {
        setIsLoading(true);
        const response = await AmplifyAPIWrapper.Get<IStackVersionInfo>("/stackversions");
        if (response?.stacks && Array.isArray(response.stacks)) {
          setStackInfo(response.stacks);
        }
        setIsLoading(false);
      };
      getStackInfo();
    }
  }, [userIsVMO]);

  return (
    <OCAPIVersionInfoWindow container direction="column" wrap="nowrap" alignItems="flex-start">
      <SpinnerProgress className="loading_spinner" loading={isLoading} message="Loading API Version Info" />
      <Grid container direction="column" style={{ padding: 20 }}>
        {userIsVMO
          ? stackInfo?.map((entry, index) => (
              <Grid key={`${entry.stack}_${index}`} container direction="column" style={{ marginBottom: 20 }}>
                <Typography color="textPrimary">{entry.stack}</Typography>
                <Grid container>
                  <Grid item style={{ width: 100 }}>
                    Version:
                  </Grid>
                  {entry.version ?? "Unknown"}
                </Grid>
                <Grid container>
                  <Grid item style={{ width: 100 }}>
                    Status:
                  </Grid>
                  {entry.status ?? "Unknown"}
                </Grid>
                <Grid container>
                  <Grid item style={{ width: 100 }}>
                    Last Updated:
                  </Grid>
                  {entry.lastUpdated ? `${formatDateString(entry.lastUpdated, "Etc/UTC", true, false)}` : "Unknown"}
                </Grid>
              </Grid>
            ))
          : "You do not have the permissions required to view this content, please notify administrator for assistance."}
      </Grid>
    </OCAPIVersionInfoWindow>
  );
};
